import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { ExtendDirection } from '@/modules/common';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { TeamMemberChangeDeletionConflictData } from '../types/team-member-change-deletion';
import { TeamMemberChangeLogEntry } from '../types/team-member-changes';
import {
  analyzeChangeDeletion,
  buildExtensionOptions,
  findNextChange,
  findPreviousChange,
} from '../utils/team-member-changes/team-member-change-deletion.utils';
import { useDeleteTeamMemberChange } from './use-delete-team-member-change';

interface UseTeamMemberChangeDeletionParams {
  changesItems?: TeamMemberChangeLogEntry[];
  teamMemberId: number;
}

/**
 * Manages the delete change workflow including conflict analysis,
 * simple confirmation when unambiguous, and modal state for conflict resolution.
 */
export const useTeamMemberChangeDeletion = ({
  changesItems,
  teamMemberId,
}: UseTeamMemberChangeDeletionParams) => {
  const { t } = useTranslation();
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const [deleteConflictData, setDeleteConflictData] =
    useState<TeamMemberChangeDeletionConflictData | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSimpleDeleteConfirm, setShowSimpleDeleteConfirm] = useState(false);
  const [changeToDelete, setChangeToDelete] = useState<{
    change: TeamMemberChangeLogEntry;
    extendDirection: ExtendDirection;
  } | null>(null);
  const deleteChangeMutation = useDeleteTeamMemberChange();

  // Keep a stable ref so handleDeleteClick doesn't change when items refresh,
  // which would recreate columns and cause avatar blink on every re-render.
  const changesItemsRef = useRef(changesItems);
  changesItemsRef.current = changesItems;

  const handleDeleteClick = useCallback(
    async (row: TeamMemberChangeLogEntry) => {
      const changesItems = changesItemsRef.current;
      if (!changesItems || !teamMemberId) return;

      const conflictAnalysis = analyzeChangeDeletion(changesItems, row, t);

      if (!conflictAnalysis) {
        // No conflict — either 0 or 1 extension option exists.
        // Use that option's direction, defaulting to Previous (extends the earlier change).
        const previousChange = findPreviousChange(changesItems, row);
        const options = buildExtensionOptions(
          t,
          previousChange,
          findNextChange(changesItems, row),
          row
        );
        const extendDirection =
          options.length === 1 ? options[0].direction : ExtendDirection.Previous;

        setChangeToDelete({ change: row, extendDirection });
        setShowSimpleDeleteConfirm(true);
      } else {
        // Show modal for user to choose resolution
        setDeleteConflictData(conflictAnalysis);
        setShowDeleteModal(true);
      }
    },
    [teamMemberId, t]
  );

  const handleSimpleDeleteConfirm = useCallback(
    async (closeModal: () => void) => {
      if (!changeToDelete || !teamMemberId) return;

      try {
        await deleteChangeMutation.mutateAsync({
          teamMemberId,
          section: changeToDelete.change.type,
          stateDeltaId: changeToDelete.change.id,
          extendDirection: changeToDelete.extendDirection,
        });
        displayToastSuccess(t('authenticated.team:details.tabs.changes.deleteSuccess'));
        closeModal();
        setChangeToDelete(null);
      } catch (error) {
        displayToastErrorFromApiWithFallback(
          error,
          'authenticated.team:details.tabs.changes.deleteModal.deleteError'
        );
      }
    },
    [changeToDelete, teamMemberId, deleteChangeMutation, t, displayToastErrorFromApiWithFallback]
  );

  const handleCloseSimpleDeleteConfirm = useCallback(() => {
    setShowSimpleDeleteConfirm(false);
    setChangeToDelete(null);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteConflictData(null);
    displayToastSuccess(t('authenticated.team:details.tabs.changes.deleteSuccess'));
  }, [t]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteConflictData(null);
  }, []);

  return {
    handleDeleteClick,
    handleSimpleDeleteConfirm,
    handleCloseSimpleDeleteConfirm,
    showSimpleDeleteConfirm,
    isSimpleDeletePending: deleteChangeMutation.isPending,
    handleDeleteSuccess,
    showDeleteModal,
    deleteConflictData,
    handleCloseDeleteModal,
  };
};
